import type {
  BudgetView,
  CostPreviewResult,
  AutoRechargeSettings,
  MetaMigrationPreview,
  BillingOverview,
  InvoiceInfo,
  PlanChangeOutcome,
  PlanInfo,
  UsageMetersInfo,
  AnnouncementBanner,
  ReactivateResult,
  AddOnActivationResult,
  AddOnInfo,
  CadenceChangeResult,
  EntitlementState,
  LedgerPage,
  WalletInfo,
} from "../types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type BillingApiMethods = Pick<
  ApiClient,
  | "getWallet"
  | "getBillingOverview"
  | "previewMetaMigration"
  | "confirmMetaMigration"
  | "getAutoRecharge"
  | "updateAutoRecharge"
  | "listBudgets"
  | "setBudget"
  | "removeBudget"
  | "costPreview"
  | "listPlans"
  | "changePlan"
  | "listInvoices"
  | "getUsageMeters"
  | "listAnnouncements"
  | "listAddOns"
  | "activateAddOn"
  | "changeBillingCadence"
  | "reactivateSubscription"
  | "getEntitlement"
  | "getEntitlements"
  | "listLedger"
>;

export function createBillingApi({ request, ws }: HttpTransport): BillingApiMethods {
  return {
    getWallet: (id) => request<WalletInfo>("GET", `${ws(id)}/billing/wallet`),
    getBillingOverview: (id) => request<BillingOverview>("GET", `${ws(id)}/billing`),
    previewMetaMigration: (id) =>
      request<MetaMigrationPreview>("POST", `${ws(id)}/billing/meta/migration/preview`),
    confirmMetaMigration: (id) =>
      request<{ effectiveAt: string }>("POST", `${ws(id)}/billing/meta/migration/confirm`),
    getAutoRecharge: async (id) =>
      (
        await request<{ autoRecharge: AutoRechargeSettings }>(
          "GET",
          `${ws(id)}/billing/wallet/auto-recharge`,
        )
      ).autoRecharge,
    updateAutoRecharge: async (id, input) =>
      (
        await request<{ autoRecharge: AutoRechargeSettings }>(
          "PUT",
          `${ws(id)}/billing/wallet/auto-recharge`,
          input,
        )
      ).autoRecharge,
    listBudgets: async (id) =>
      (await request<{ budgets: readonly BudgetView[] }>("GET", `${ws(id)}/billing/budgets`))
        .budgets,
    setBudget: async (id, category, monthlyLimitMinor) =>
      (
        await request<{ budget: BudgetView }>("PUT", `${ws(id)}/billing/budgets`, {
          category,
          monthlyLimitMinor,
        })
      ).budget,
    removeBudget: async (id, category) => {
      await request<unknown>("DELETE", `${ws(id)}/billing/budgets/${category}`);
    },
    costPreview: (id, input) =>
      request<CostPreviewResult>("POST", `${ws(id)}/billing/cost-preview`, input),
    listPlans: async () => (await request<{ plans: PlanInfo[] }>("GET", "/v1/billing/plans")).plans,
    changePlan: (id, direction, planTier) =>
      request<PlanChangeOutcome>(
        "POST",
        `${ws(id)}/billing/subscription/${direction}`,
        { planTier },
        undefined,
        { "idempotency-key": `plan-${direction}-${planTier}-${id}-${String(Date.now())}` },
      ),
    listInvoices: async (id) =>
      (await request<{ invoices: InvoiceInfo[] }>("GET", `${ws(id)}/billing/invoices`)).invoices,
    listAnnouncements: async (id) =>
      (
        await request<{ banners: AnnouncementBanner[] }>(
          "GET",
          `/v1/announcements?workspaceId=${encodeURIComponent(id)}`,
        )
      ).banners,
    getUsageMeters: (id) => request<UsageMetersInfo>("GET", `${ws(id)}/billing/usage`),
    listAddOns: async (id) =>
      (await request<{ addOns: AddOnInfo[] }>("GET", `${ws(id)}/billing/addons`)).addOns,
    activateAddOn: (id, key) =>
      request<AddOnActivationResult>(
        "POST",
        `${ws(id)}/billing/addons/${encodeURIComponent(key)}/activate`,
        undefined,
        undefined,
        { "idempotency-key": `addon-${key}-${id}-${String(Date.now())}` },
      ),
    changeBillingCadence: (id, cycle) =>
      request<CadenceChangeResult>(
        "POST",
        `${ws(id)}/billing/subscription/cadence`,
        { cycle },
        undefined,
        { "idempotency-key": `cadence-${cycle}-${id}-${String(Date.now())}` },
      ),
    reactivateSubscription: (id) =>
      request<ReactivateResult>(
        "POST",
        `${ws(id)}/billing/subscription/reactivate`,
        undefined,
        undefined,
        { "idempotency-key": `reactivate-${id}-${String(Date.now())}` },
      ),
    getEntitlement: (id, featureKey, current) =>
      request<EntitlementState>(
        "GET",
        `${ws(id)}/billing/entitlements/${encodeURIComponent(featureKey)}${current === undefined ? "" : `?current=${current}`}`,
      ),
    async getEntitlements(id) {
      return (await request<{ features: Record<string, boolean> }>("GET", `${ws(id)}/entitlements`))
        .features;
    },
    listLedger: (id, cursor) =>
      request<LedgerPage>(
        "GET",
        `${ws(id)}/billing/wallet/ledger${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      ),
  };
}
